import LabeledInput from 'components/general/inputs/LabeledInput';
import LabeledSelect from 'components/general/inputs/LabeledSelect';
import { IFiniteStateMachine, TRoutine } from 'components/general/types/cdh';
import useStyles from 'components/general/wizards/WizardSegment/styles';
import { CSSProperties, ChangeEvent, memo, useContext } from 'react';
import { useReactFlow } from 'react-flow-renderer';
import theme from 'theme';
import { RoutineVables, TRoutineTypes } from 'utils/vable';
import { RoutineContext, useRoutineState } from '../RoutinesWidget';
import CombinationalLogicForm from './CombinationalLogicForm';
import FiniteStateMachineForm from './FiniteStateMachineForm';
import ScheduleForm from './ScheduleForm';

export const defaultRoutine = (): Partial<TRoutine> => ({
  name: '',
  type: 'Routine',
  defaultSubroutine: undefined,
  subroutines: [],
  parentRoutines: [],
});

export const additionalDefaults = (type: TRoutineTypes): Partial<TRoutine> => {
  const defaults: { [key in TRoutineTypes]: Partial<TRoutine> } = {
    Routine: {},
    CombinationalLogic: {
      type: 'CombinationalLogic',
      logicalConfigurations: [],
    },
    FiniteStateMachine: {
      type: 'FiniteStateMachine',
      activeSubroutine: undefined,
      transitions: [],
    },
    FixedSchedule: {
      type: 'FixedSchedule',
      scheduleData: {},
    },
    RelativeSchedule: {
      type: 'RelativeSchedule',
      scheduleData: {},
    },
  };
  return defaults[type];
};

export interface IRoutineMenuConfig {
  open: boolean;
  action: 'create' | 'edit';
  ids: string[];
}

export interface IRoutineFormProps {
  id: string;
  className?: string;
  style?: CSSProperties;
}

const RoutineForm = ({ className, style, id }: IRoutineFormProps) => {
  const { updateType } = useContext(RoutineContext);
  const { setEdges } = useReactFlow();
  const { data: routine, setValue } = useRoutineState<TRoutine>(id);
  const { classes, cx } = useStyles();

  return (
    <div className={className} style={style}>
      <div className={cx(classes.inputs, classes.inputGroup)}>
        <LabeledInput
          value={routine.name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value, 'name')}
          label="Name"
          type="text"
          placeholder="Name"
          autoFocus
        />
        <LabeledSelect
          value={{
            label: RoutineVables.RoutineTypes[routine.type as TRoutineTypes].label,
            value: routine.type,
          }}
          onChange={({ value }: { value: TRoutineTypes }) => {
            const addEdges = updateType(id, value);
            setEdges((edges) => {
              let newEdges = edges.filter(
                (edge) => edge.type === 'StateTransition' || edge.source !== id
              );
              if (routine.type === 'FiniteStateMachine') {
                newEdges = newEdges.filter(
                  (edge) => !(routine as IFiniteStateMachine).transitions.includes(edge.id)
                );
              }
              return newEdges.concat(addEdges);
            });
          }}
          label="Type"
          options={RoutineVables.RoutineTypes.options}
          menuPosition="fixed"
        />
      </div>
      {routine.type !== 'Routine' &&
        (routine.subroutines.length > 0 ? (
          <div className={classes.inputGroup}>
            {routine.type === 'CombinationalLogic' && <CombinationalLogicForm id={routine.id} />}
            {routine.type === 'FiniteStateMachine' && <FiniteStateMachineForm id={routine.id} />}
            {(routine.type === 'RelativeSchedule' || routine.type === 'FixedSchedule') && (
              <ScheduleForm id={routine.id} />
            )}
          </div>
        ) : (
          <div
            className={classes.inputGroup}
            style={{ textAlign: 'center', color: theme.palette.text.secondary }}
          >
            Add subroutines by connecting them to this routine
          </div>
        ))}
    </div>
  );
};

export default memo(RoutineForm);
